import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import dnkIcon from "../../../media/dnk.png";
import {
  findLatestSmartDiagnosticsDoc,
  mapSmartDiagnosticsDocData,
  SMART_DIAGNOSTICS_FORM_DEFAULT,
  SmartDiagnosticsPrintSheet,
} from "./PatientSmartDiagnosticsPrint";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";
import "./PatientPlanPrint.css";
import "./PatientPlanPrintOtherFormat.css";
import "./PatientSmartDiagnosticsPrint.css";

const formatDate = (dateString) => {
  if (!dateString || dateString === "—") return "—";
  const [datePart] = String(dateString).split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
};

const parseDocData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
};

const displayValue = (value) => {
  const text = value == null ? "" : String(value).trim();
  return text || "\u00A0";
};

const normalizeGroupName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase();

const INSTRUMENTAL_DIAGNOSTICS = [
  "plan.printOther.instrumentalItems.visbody",
  "plan.printOther.instrumentalItems.abdominal",
  "plan.printOther.instrumentalItems.liver",
  "plan.printOther.instrumentalItems.thyroid",
  "plan.printOther.instrumentalItems.kidneys",
  "plan.printOther.instrumentalItems.neckVessels",
  "plan.printOther.instrumentalItems.legVessels",
  "plan.printOther.instrumentalItems.oligoScan",
];

const getGroupByName = (plan, groupName) => {
  const groups = plan?.result?.groups || [];
  return (
    groups.find(
      (group) => normalizeGroupName(group?.name?.value) === groupName,
    ) || null
  );
};

const getGroupsByTypeOrName = (plan, { type, name, nameIncludes } = {}) => {
  const groups = plan?.result?.groups || [];
  const normalizedType = type ? String(type).toLowerCase() : "";
  const normalizedName = name ? normalizeGroupName(name) : "";
  const normalizedIncludes = nameIncludes
    ? normalizeGroupName(nameIncludes)
    : "";
  return groups.filter((group) => {
    const groupType = normalizeGroupName(group?.type?.value ?? group?.type);
    const groupName = normalizeGroupName(group?.name?.value ?? group?.name);

    if (normalizedType && groupType === normalizedType) return true;
    if (normalizedName && groupType === normalizedName) return true;
    if (normalizedName && groupName === normalizedName) return true;
    if (normalizedName && groupName.includes(normalizedName)) return true;
    if (normalizedIncludes && groupName.includes(normalizedIncludes))
      return true;
    return false;
  });
};

const getItemDisplayName = (item) =>
  String(item?.name?.value ?? item?.name ?? "").trim();

const isAppointmentMarked = (apt) => {
  if (!apt) return false;
  const hasAdmission =
    apt?.id_admission?.value !== undefined &&
    String(apt.id_admission.value) !== "0";
  const isScheduled = apt?.status?.value === "scheduled";
  return hasAdmission || isScheduled;
};

const getCheckupItemsFromPlan = (plan) => {
  const checkupGroup = getGroupByName(plan, "чекапы");
  if (!checkupGroup) return [];

  return (checkupGroup.items || [])
    .map((item) => {
      const name = String(item?.name?.value || "").trim();
      if (!name) return null;
      const subitems = Array.isArray(item?.subitems)
        ? item.subitems
            .map((sub) => String(sub?.name?.value || "").trim())
            .filter(Boolean)
        : [];
      return { name, subitems };
    })
    .filter(Boolean);
};

const getInfusionTherapyFromPlan = (plan) => {
  const infusionGroup = getGroupByName(plan, "инфузионная терапия");
  if (!infusionGroup) {
    return { items: [], proceduresCount: 0 };
  }

  const items = (infusionGroup.items || [])
    .map((item) => {
      const name = String(item?.name?.value || "").trim();
      if (!name) return null;
      const composition = String(item?.composition?.value || "").trim();
      const subitems = Array.isArray(item?.subitems)
        ? item.subitems
            .map((sub) => {
              const subName = String(sub?.name?.value || "").trim();
              if (!subName) return null;
              const subComposition = String(
                sub?.composition?.value || "",
              ).trim();
              return {
                name: subName,
                composition: subComposition,
              };
            })
            .filter(Boolean)
        : [];
      return { name, composition, subitems };
    })
    .filter(Boolean);

  const markedAppointmentNumbers = new Set();
  (infusionGroup.items || []).forEach((item) => {
    (item?.appointments || []).forEach((apt) => {
      if (!isAppointmentMarked(apt)) return;
      const number = parseInt(apt?.appointment_number?.value, 10);
      if (Number.isFinite(number) && number > 0) {
        markedAppointmentNumbers.add(number);
      }
    });
  });

  return {
    items,
    proceduresCount: markedAppointmentNumbers.size,
  };
};

const getOralMedicationsFromPlan = (plan) => {
  const oralGroups = getGroupsByTypeOrName(plan, {
    type: "therapy",
    name: "пероральные лекарственные препараты",
    nameIncludes: "пероральн",
  });
  if (oralGroups.length === 0) return [];

  return oralGroups.flatMap((group) =>
    (Array.isArray(group.items) ? group.items : [])
      .map((item) => {
        const name = getItemDisplayName(item);
        if (!name) return null;
        return { name };
      })
      .filter(Boolean),
  );
};

const MIN_ORAL_MEDICATION_ROWS = 4;
const SCHEDULE_WEEKS_COUNT = 12;
const SCHEDULE_ROWS = [
  {
    key: "injections",
    labelKey: "plan.printOther.scheduleRows.injections",
    groupName: "процедуры",
    icon: "syringe",
  },
  {
    key: "infusion",
    labelKey: "plan.printOther.scheduleRows.infusion",
    groupName: "инфузионная терапия",
    nameIncludes: "инфузионн",
    icon: "drop",
  },
  {
    key: "peptides",
    labelKey: "plan.printOther.scheduleRows.peptides",
    groupName: "пептиды",
    icon: "dna",
  },
  {
    key: "consultation",
    labelKey: "plan.printOther.scheduleRows.consultation",
    groupName: "консультация",
    icon: "doctor",
    highlightMilestones: true,
  },
  {
    key: "checkup",
    labelKey: "plan.printOther.scheduleRows.checkup",
    groupName: "чекапы",
    icon: "flask",
    highlightMilestones: true,
  },
  {
    key: "visbody",
    labelKey: "plan.printOther.scheduleRows.visbody",
    icon: "clipboard",
    highlightMilestones: true,
  },
];

const getMarkedAppointmentNumbersFromItem = (item) => {
  const marked = new Set();
  (item?.appointments || []).forEach((apt) => {
    if (!isAppointmentMarked(apt)) return;
    const number = parseInt(apt?.appointment_number?.value, 10);
    if (Number.isFinite(number) && number > 0) {
      marked.add(number);
    }
  });
  return marked;
};

const getMarkedAppointmentNumbersFromGroup = (group) => {
  const marked = new Set();
  (group?.items || []).forEach((item) => {
    getMarkedAppointmentNumbersFromItem(item).forEach((number) => {
      marked.add(number);
    });
  });
  return marked;
};

const getMaxAppointmentNumberFromPlan = (plan) => {
  let maxNumber = 0;
  const planLimit = Number(plan?.result?.count_appointment?.value);
  if (Number.isFinite(planLimit) && planLimit > maxNumber) {
    maxNumber = planLimit;
  }

  (plan?.result?.groups || []).forEach((group) => {
    const groupLimit = Number(group?.count_appointment?.value);
    if (Number.isFinite(groupLimit) && groupLimit > maxNumber) {
      maxNumber = groupLimit;
    }
    getMarkedAppointmentNumbersFromGroup(group).forEach((number) => {
      if (number > maxNumber) maxNumber = number;
    });
  });

  return maxNumber;
};

const resolveScheduleIcon = (groupName, itemName = "") => {
  const haystack = `${normalizeGroupName(groupName)} ${normalizeGroupName(itemName)}`;

  if (
    haystack.includes("visbody") ||
    haystack.includes("inbody") ||
    haystack.includes("биоимпеданс")
  ) {
    return { icon: "clipboard", highlightMilestones: true };
  }
  if (
    haystack.includes("чекап") ||
    haystack.includes("check-up") ||
    haystack.includes("checkup")
  ) {
    return { icon: "flask", highlightMilestones: true };
  }
  if (haystack.includes("консультац")) {
    return { icon: "doctor", highlightMilestones: true };
  }
  if (haystack.includes("инфузионн")) {
    return { icon: "drop" };
  }
  if (haystack.includes("пептид")) {
    return { icon: "dna" };
  }
  if (
    haystack.includes("инъекц") ||
    haystack.includes("glp") ||
    haystack.includes("процедур")
  ) {
    return { icon: "syringe" };
  }
  if (haystack.includes("пероральн") || haystack.includes("препарат")) {
    return { icon: "pill" };
  }
  return { icon: "clipboard", highlightMilestones: false };
};

const getItemSubitems = (item) =>
  Array.isArray(item?.subitems)
    ? item.subitems
        .map((sub) => {
          const name = String(sub?.name?.value ?? sub?.name ?? "").trim();
          if (!name) return null;
          return {
            name,
            composition: String(
              sub?.composition?.value ?? sub?.composition ?? "",
            ).trim(),
          };
        })
        .filter(Boolean)
    : [];

const getPlanScheduleSections = (plan, t) => {
  const groups = Array.isArray(plan?.result?.groups) ? plan.result.groups : [];

  return groups
    .map((group, groupIndex) => {
      const groupName = String(group?.name?.value ?? group?.name ?? "").trim();
      if (!groupName) return null;

      const items = (Array.isArray(group.items) ? group.items : [])
        .map((item, itemIndex) => {
          const name = getItemDisplayName(item);
          if (!name) return null;

          const iconMeta = resolveScheduleIcon(groupName, name);
          return {
            key: `plan-item-${group?.id?.value || groupIndex}-${item?.id?.value || itemIndex}`,
            name,
            composition: String(
              item?.composition?.value ?? item?.composition ?? "",
            ).trim(),
            subitems: getItemSubitems(item),
            markedWeeks: getMarkedAppointmentNumbersFromItem(item),
            ...iconMeta,
          };
        })
        .filter(Boolean);

      if (items.length === 0) return null;

      return {
        key: `plan-group-${group?.id?.value || groupIndex}`,
        groupName: getGroupTypeLabel(t, group),
        items,
      };
    })
    .filter(Boolean);
};

const getScheduleMarkedWeeks = (plan, row) => {
  if (!row.groupName && !row.nameIncludes) {
    return new Set();
  }

  const groups = getGroupsByTypeOrName(plan, {
    name: row.groupName,
    nameIncludes: row.nameIncludes,
  });

  const marked = new Set();
  groups.forEach((group) => {
    getMarkedAppointmentNumbersFromGroup(group).forEach((number) => {
      marked.add(number);
    });
  });
  return marked;
};

const getRecommendationsList = (recommendations, t) => {
  const result = recommendations?.result;
  const toItem = (item) => {
    if (!item || typeof item !== "object") return null;
    const title = String(item?.title?.value ?? item?.title ?? "").trim();
    const html = String(
      item?.description?.value ?? item?.description ?? "",
    ).trim();
    if (!title && !html) return null;
    return {
      id: item?.id?.value ?? item?.id,
      title: title || t("plan.buttons.recommendations"),
      html,
    };
  };

  if (Array.isArray(result)) {
    return result.map(toItem).filter(Boolean);
  }

  const single = toItem(result);
  return single ? [single] : [];
};

const chunkRecommendationRows = (items) => {
  const rows = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2));
  }
  return rows;
};

const isMilestoneWeek = (week) => week === 1 || week % 4 === 0;

const ScheduleRowIcon = ({ type }) => {
  if (type === "syringe") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M14.5 3.5l6 6M13 5l6 6"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M12.2 6.8L5.4 13.6a3.2 3.2 0 0 0-.1 4.4l.7.7a3.2 3.2 0 0 0 4.4-.1l6.8-6.8"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M4 20l2.2-2.2M9.2 11.2l3.6 3.6"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "drop") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3.5c0 0-6 7.2-6 11.2a6 6 0 0 0 12 0C18 10.7 12 3.5 12 3.5z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "dna") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M8 3c4 4 4 14 8 18M16 3c-4 4-4 14-8 18"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M8.8 8.5h6.4M8.8 12h6.4M8.8 15.5h6.4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "doctor") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle
          cx="12"
          cy="8"
          r="3.2"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M5.5 20c1.2-3.4 3.4-5 6.5-5s5.3 1.6 6.5 5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "flask") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M9 3h6M10 3v5.2L6.4 16.2A3.6 3.6 0 0 0 9.7 21h4.6a3.6 3.6 0 0 0 3.3-4.8L14 8.2V3"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.2 14.5h7.6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "pill") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect
          x="3.4"
          y="8.4"
          width="17.2"
          height="7.2"
          rx="3.6"
          stroke="currentColor"
          strokeWidth="1.7"
          transform="rotate(-28 12 12)"
        />
        <path
          d="M9.2 8.6l5.6 6.8"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="5"
        y="4"
        width="14"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M8.5 9h7M8.5 12.5h7M8.5 16h4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
};

const REGIME_ITEMS = [
  {
    key: "meals",
    icon: "clock",
    textKey: "plan.printOther.regime.meals",
  },
  {
    key: "sleep",
    icon: "moon",
    textKey: "plan.printOther.regime.sleep",
  },
  {
    key: "mindful",
    icon: "lotus",
    textKey: "plan.printOther.regime.mindful",
  },
  {
    key: "portions",
    icon: "plate",
    textKey: "plan.printOther.regime.portions",
  },
  {
    key: "limit",
    icon: "forbid",
    textKey: "plan.printOther.regime.limit",
  },
];

const RegimeItemIcon = ({ type }) => {
  if (type === "clock") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle
          cx="12"
          cy="12"
          r="8.4"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M12 7.6V12l3.2 2"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "moon") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M15.2 4.6A8.3 8.3 0 1 0 19.4 16.6 6.8 6.8 0 0 1 15.2 4.6z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "lotus") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 18.4C10.2 13.8 10.2 9.2 12 5.6c1.8 3.6 1.8 8.2 0 12.8z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M12 18.2C8.8 15.4 6.2 11.2 7 7.6c2.6 1.8 4.2 5.4 5 10.6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M12 18.2c3.2-2.8 5.8-7 5-10.6-2.6 1.8-4.2 5.4-5 10.6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M12 18.2C8.2 16.8 4.6 14.8 3.6 11.6c3 .6 6.2 3.4 8.4 6.6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M12 18.2c3.8-1.4 7.4-3.4 8.4-6.6-3 .6-6.2 3.4-8.4 6.6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M5.2 19.2c2.2-.9 4.5-1.4 6.8-1.4s4.6.5 6.8 1.4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "plate") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M3.4 5.2v4.4M2.2 5.2v3.2M4.6 5.2v3.2M3.4 9.6v9.2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <ellipse
          cx="12"
          cy="13.2"
          rx="4.4"
          ry="4.6"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <ellipse
          cx="12"
          cy="13.2"
          rx="2.1"
          ry="2.2"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <path
          d="M20.4 5.2v13.6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M20.4 5.2c1.7 1.8 1.7 5.4 0 7.4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M6.2 17.8L17.8 6.2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M8.4 13.6c.6-1.6 1.8-2.6 3.2-2.4 1.1.1 1.6 1 2.6 1.1 1.2.1 2.2-.8 2.6-1.8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="M9.6 11.2c-.5.2-1.1.1-1.5-.4M16.4 10.2c.4-.4.6-1 .4-1.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <rect
        x="10.4"
        y="14.2"
        width="1.5"
        height="2.8"
        rx="0.4"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <path d="M10.2 14.2h1.9v.7H10.2z" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
};

const PlanPrintOtherLogoIcon = () => (
  <svg
    className="plan-print-other-logo-icon"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M12 2.4C12 2.4 4.6 10.1 4.6 15.2a7.4 7.4 0 0 0 14.8 0C19.4 10.1 12 2.4 12 2.4z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 5.8C12 5.8 7.2 11.2 7.2 14.9a4.8 4.8 0 0 0 9.6 0C16.8 11.2 12 5.8 12 5.8z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 9.2C12 9.2 9.4 12.4 9.4 14.7a2.6 2.6 0 0 0 5.2 0C14.6 12.4 12 9.2 12 9.2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const ImportantClipboardIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="24" cy="24" r="21.2" stroke="currentColor" strokeWidth="1.35" />
    <rect
      x="16.2"
      y="15.4"
      width="15.6"
      height="19.2"
      rx="2.2"
      stroke="currentColor"
      strokeWidth="1.45"
    />
    <rect
      x="20"
      y="12.8"
      width="8"
      height="4.4"
      rx="1.2"
      stroke="currentColor"
      strokeWidth="1.35"
    />
    <path
      d="M20.6 25.2l2.6 2.6 5.4-5.6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ImportantBranchIcon = () => (
  <svg viewBox="0 0 72 80" fill="none" aria-hidden="true">
    <path
      d="M16 72c8-14 18-26 28-42 6-10 12-22 16-32"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <path
      d="M28 58c-6-1-12 2-16 8"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M32 50c6-1 11-6 13-12"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M38 40c-6 0-12 4-16 10"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M44 30c6-2 11-8 12-14"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M48 22c-5 1-11 5-14 11"
      stroke="currentColor"
      strokeWidth="1.15"
      strokeLinecap="round"
    />
    <ellipse
      cx="14.8"
      cy="63.2"
      rx="4.2"
      ry="7.4"
      transform="rotate(-28 14.8 63.2)"
      stroke="currentColor"
      strokeWidth="1.15"
    />
    <ellipse
      cx="41.6"
      cy="42.4"
      rx="4"
      ry="7"
      transform="rotate(28 41.6 42.4)"
      stroke="currentColor"
      strokeWidth="1.15"
    />
    <ellipse
      cx="25.2"
      cy="47.8"
      rx="3.8"
      ry="6.6"
      transform="rotate(-34 25.2 47.8)"
      stroke="currentColor"
      strokeWidth="1.15"
    />
    <ellipse
      cx="52.6"
      cy="22.4"
      rx="3.6"
      ry="6.2"
      transform="rotate(32 52.6 22.4)"
      stroke="currentColor"
      strokeWidth="1.15"
    />
    <ellipse
      cx="37.4"
      cy="28.6"
      rx="3.4"
      ry="5.8"
      transform="rotate(-30 37.4 28.6)"
      stroke="currentColor"
      strokeWidth="1.15"
    />
    <ellipse
      cx="58.2"
      cy="10.8"
      rx="3"
      ry="5.2"
      transform="rotate(38 58.2 10.8)"
      stroke="currentColor"
      strokeWidth="1.1"
    />
    <path
      d="M62 18l.7 2.4 2.4.7-2.4.7-.7 2.4-.7-2.4-2.4-.7 2.4-.7z"
      fill="currentColor"
    />
    <path
      d="M54 6l.55 1.8 1.8.55-1.8.55-.55 1.8-.55-1.8-1.8-.55 1.8-.55z"
      fill="currentColor"
    />
    <path
      d="M66 8l.4 1.35 1.35.4-1.35.4-.4 1.35-.4-1.35-1.35-.4 1.35-.4z"
      fill="currentColor"
    />
    <path
      d="M48 12l.35 1.1 1.1.35-1.1.35-.35 1.1-.35-1.1-1.1-.35 1.1-.35z"
      fill="currentColor"
    />
  </svg>
);

function PatientPlanPrintOtherFormat() {
  const { t } = useTranslation("patient");
  const { patientId, planId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [primaryVisit, setPrimaryVisit] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [includeSmartDiagnostics, setIncludeSmartDiagnostics] = useState(false);
  const [smartDiagnosticsForm, setSmartDiagnosticsForm] = useState({
    ...SMART_DIAGNOSTICS_FORM_DEFAULT,
  });
  const [smartDiagnosticsMeta, setSmartDiagnosticsMeta] = useState(null);
  const [printSections, setPrintSections] = useState({
    diagnosis: true,
    checkup: true,
    infusion: true,
    meds: true,
    schedule: true,
    scheduleDetailed: true,
    recommendations: true,
    instrumental: true,
    scheduleHideEmpty: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const parsedPlanId = Number(planId);
        const currentPlanId =
          Number.isFinite(parsedPlanId) && parsedPlanId > 0 ? parsedPlanId : 0;

        const [planData, appointmentsData, docsListData, recommendationsData] =
          await Promise.all([
            GetData({
              module: "patient",
              block: "treatment_plan",
              id_patient: patientId,
              id_plan: currentPlanId,
            }),
            GetData({
              module: "patient",
              block: "list_appointments",
              id_patient: patientId,
            }),
            GetData({
              module: "patient",
              block: "patient_docs",
              id_patient: patientId,
              action: "list",
            }),
            GetData({
              module: "patient",
              block: "recommendations",
              id_patient: patientId,
              id_plan: currentPlanId,
              id_recommendation: 0,
            }),
          ]);

        setPlan(planData);
        setAppointments(appointmentsData);
        setRecommendations(recommendationsData);

        const docs = Array.isArray(docsListData?.result)
          ? docsListData.result
          : [];
        const primaryDoc = docs
          .filter((doc) => Number(doc?.template_id) === 1)
          .sort((a, b) => {
            const da = Date.parse(a?.created_date) || 0;
            const db = Date.parse(b?.created_date) || 0;
            return db - da;
          })[0];

        if (primaryDoc?.id) {
          const primaryDocData = await GetData({
            module: "patient",
            block: "patient_docs",
            action: "get",
            id_patient: patientId,
            id: primaryDoc.id,
          });
          setPrimaryVisit(parseDocData(primaryDocData?.result?.data));
        } else {
          setPrimaryVisit(null);
        }

        const smartDoc = findLatestSmartDiagnosticsDoc(docs);
        if (smartDoc?.id) {
          const smartDocData = await GetData({
            module: "patient",
            block: "patient_docs",
            action: "get",
            id_patient: patientId,
            id: smartDoc.id,
          });
          setSmartDiagnosticsForm(
            mapSmartDiagnosticsDocData(smartDocData?.result?.data),
          );
          setSmartDiagnosticsMeta(smartDocData?.result || smartDoc);
        } else {
          setSmartDiagnosticsForm({ ...SMART_DIAGNOSTICS_FORM_DEFAULT });
          setSmartDiagnosticsMeta(null);
          setIncludeSmartDiagnostics(false);
        }
      } catch (err) {
        setError(err?.message || t("plan.print.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId, planId, t]);

  if (loading)
    return <div className="plan-print-loading">{t("plan.loading")}</div>;
  if (error)
    return (
      <div className="plan-print-error">
        {t("usi.errorPrefix", { message: error })}
      </div>
    );

  const patientFio = appointments?.result?.fio?.value || "—";
  const doctorFio = appointments?.result?.doc_name?.value || "—";
  const birthDate = formatDate(appointments?.result?.birthDate?.value || "—");
  const appointmentDate = formatDate(plan?.result?.date_start?.value || "—");
  const mainDiagnosis = displayValue(primaryVisit?.main_diagnosis);
  const mainDiagnosisMkb = displayValue(primaryVisit?.main_diagnosis_mkb);
  const concomitant = displayValue(primaryVisit?.concomitant_1);
  const checkupItems = getCheckupItemsFromPlan(plan);
  const infusionTherapy = getInfusionTherapyFromPlan(plan);
  const oralMedications = getOralMedicationsFromPlan(plan);
  const oralMedicationRows = [
    ...oralMedications,
    ...Array.from(
      {
        length: Math.max(0, MIN_ORAL_MEDICATION_ROWS - oralMedications.length),
      },
      () => ({ name: "" }),
    ),
  ];
  const infusionCompositionDisplay = ["", ""];
  const scheduleWeeks = Array.from(
    {
      length: Math.max(
        SCHEDULE_WEEKS_COUNT,
        getMaxAppointmentNumberFromPlan(plan),
      ),
    },
    (_, index) => index + 1,
  );
  const scheduleRows = SCHEDULE_ROWS.map((row) => ({
    ...row,
    markedWeeks: getScheduleMarkedWeeks(plan, row),
  }));
  const planScheduleSections = getPlanScheduleSections(plan, t);
  const visibleScheduleRows = printSections.scheduleHideEmpty
    ? scheduleRows.filter((row) => row.markedWeeks && row.markedWeeks.size > 0)
    : scheduleRows;
  const recommendationRows = chunkRecommendationRows(
    getRecommendationsList(recommendations, t),
  );
  const now = new Date();
  const signatureDate = [
    String(now.getDate()).padStart(2, "0"),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getFullYear()),
  ].join(".");

  const togglePrintSection = (sectionName) => {
    setPrintSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const numberedSectionOrder = [
    "diagnosis",
    "checkup",
    "infusion",
    "meds",
    "schedule",
    "scheduleDetailed",
    "recommendations",
  ];
  const sectionNumbers = {};
  let runningSectionNumber = 0;
  numberedSectionOrder.forEach((key) => {
    if (printSections[key]) {
      runningSectionNumber += 1;
      sectionNumbers[key] = runningSectionNumber;
    }
  });

  return (
    <div className="patient-plan-print-page patient-plan-print-other">
      <div className="plan-print-controls no-print">
        {smartDiagnosticsMeta ? (
          <label className="plan-print-section-toggle">
            <input
              type="checkbox"
              checked={includeSmartDiagnostics}
              onChange={() => setIncludeSmartDiagnostics((value) => !value)}
            />
            <span>{t("plan.printOther.smartDiagnostics")}</span>
          </label>
        ) : null}
        <Link
          to={`/patient/${patientId}/plan/${planId}/print`}
          className="button-default light">
          {t("plan.printOther.oldFormat")}
        </Link>
        <button
          type="button"
          className="button-default light"
          onClick={() => window.print()}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}>
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              width="20"
              height="20"
              style={{
                fill: "currentColor",
                transform: "scale(1.2)",
                transformOrigin: "center",
              }}>
              <path d="M19 8H5a3 3 0 0 0-3 3v4h4v4h12v-4h4v-4a3 3 0 0 0-3-3zm-3 9H8v-5h8v5zm3-7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM18 3H6v4h12V3z" />
            </svg>
            {t("plan.buttons.print")}
          </span>
        </button>
        <button
          type="button"
          className="button-default normal"
          onClick={() => window.history.back()}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}>
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              width="20"
              height="20"
              style={{ color: "currentColor" }}>
              <path
                d="M9 14L4 9l5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 9h11a4 4 0 1 1 0 8H9"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("plan.print.back")}
          </span>
        </button>
      </div>

      <header className="plan-print-other-header">
        <div className="plan-print-other-header-row">
          <div className="plan-print-other-header-brand">
            <PlanPrintOtherLogoIcon />
            <div className="plan-print-other-brand-text">
              <div className="plan-print-other-brand-name">REFRESH</div>
              <div className="plan-print-other-brand-sub">LONGEVITY CLINIC</div>
            </div>
          </div>

          <div className="plan-print-other-header-title-wrap">
            <div className="plan-print-other-header-title">
              {t("plan.printOther.headerTitle1")}
              <br />
              {t("plan.printOther.headerTitle2")}
            </div>
            <div className="plan-print-other-header-divider" aria-hidden="true">
              <span className="plan-print-other-header-divider-line" />
              <span className="plan-print-other-header-divider-diamond-wrap">
                <span className="plan-print-other-header-divider-diamond-outline" />
                <span className="plan-print-other-header-divider-diamond" />
              </span>
              <span className="plan-print-other-header-divider-line" />
            </div>
          </div>
        </div>

        <div className="plan-print-other-patient-meta">
          <div className="plan-print-other-patient-meta-row">
            <div className="plan-print-other-field plan-print-other-field--fio">
              <span className="plan-print-other-field-label">
                {t("usi.patientFio")}:
              </span>
              <span className="plan-print-other-field-value">{patientFio}</span>
            </div>
          </div>

          <div className="plan-print-other-patient-meta-row plan-print-other-patient-meta-row--triple">
            <div className="plan-print-other-field plan-print-other-field--birth">
              <span className="plan-print-other-field-label">
                {t("usi.birthDate")}:
              </span>
              <span className="plan-print-other-field-value">{birthDate}</span>
            </div>
            <div className="plan-print-other-field plan-print-other-field--appointment">
              <span className="plan-print-other-field-label">
                {t("plan.printOther.appointmentDate")}:
              </span>
              <span className="plan-print-other-field-value">
                {appointmentDate}
              </span>
            </div>
            <div className="plan-print-other-field plan-print-other-field--doctor">
              <span className="plan-print-other-field-label">
                {t("profile.fields.doctor")}:
              </span>
              <span className="plan-print-other-field-value">{doctorFio}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.diagnosis}
            onChange={() => togglePrintSection("diagnosis")}
          />
          <span>
            {sectionNumbers.diagnosis ? `${sectionNumbers.diagnosis}. ` : ""}
            {t("plan.printOther.sectionDiagnosisLabel")}
          </span>
        </label>
      </div>

      {printSections.diagnosis && (
        <section className="plan-print-other-section plan-print-other-section-first">
          <div className="plan-print-other-section-rail" aria-hidden="true">
            <svg
              className="plan-print-other-section-rail-icon"
              viewBox="0 0 24 24"
              fill="none">
              <rect
                x="6"
                y="3"
                width="15"
                height="21"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M9 3.5h6v2.2H9V3.5z"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M9 10h6M9 13.5h6M9 17h4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <div className="plan-print-other-section-rail-number">
              {sectionNumbers.diagnosis}
            </div>
          </div>

          <div className="plan-print-other-section-body">
            <div className="plan-print-other-section-title">
              {sectionNumbers.diagnosis}. {t("plan.printOther.sectionDiagnosis")}
            </div>

            <div className="plan-print-other-section-row plan-print-other-section-row--diag">
              <div className="plan-print-other-field plan-print-other-field--main-diag">
                <span className="plan-print-other-field-label">
                  {t("plan.printOther.mainDiagnosis")}:
                </span>
                <span className="plan-print-other-field-value">
                  {mainDiagnosis}
                </span>
              </div>
              <div className="plan-print-other-field plan-print-other-field--mkb">
                <span className="plan-print-other-field-label">
                  {t("plan.printOther.mkb")}:
                </span>
                <span className="plan-print-other-field-value">
                  {mainDiagnosisMkb}
                </span>
              </div>
            </div>

            <div className="plan-print-other-section-row">
              <div className="plan-print-other-field plan-print-other-field--full">
                <span className="plan-print-other-field-label">
                  {t("plan.printOther.concomitant")}:
                </span>
                <span className="plan-print-other-field-value">
                  {concomitant}
                </span>
              </div>
            </div>

            <div className="plan-print-other-section-row plan-print-other-section-row--stack">
              <div className="plan-print-other-field-label">
                {t("plan.printOther.metabolicFactors")}:
              </div>
              <div
                className="plan-print-other-section-line"
                aria-hidden="true"
              />
              <div
                className="plan-print-other-section-line"
                aria-hidden="true"
              />
            </div>
          </div>
        </section>
      )}

      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.checkup}
            onChange={() => togglePrintSection("checkup")}
          />
          <span>
            {sectionNumbers.checkup ? `${sectionNumbers.checkup}. ` : ""}
            {t("plan.printOther.sectionCheckupLabel")}
          </span>
        </label>
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.instrumental}
            onChange={() => togglePrintSection("instrumental")}
            disabled={!printSections.checkup}
          />
          <span>{t("plan.printOther.instrumentalLabel")}</span>
        </label>
      </div>

      {printSections.checkup && (
        <section className="plan-print-other-section plan-print-other-section-second">
          <div className="plan-print-other-section-rail" aria-hidden="true">
            <svg
              className="plan-print-other-section-rail-icon"
              viewBox="0 0 24 24"
              fill="none">
              <path
                d="M9 3h6M10 3v5.2L6.4 16.2A3.6 3.6 0 0 0 9.7 21h4.6a3.6 3.6 0 0 0 3.3-4.8L14 8.2V3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.2 14.5h7.6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            <div className="plan-print-other-section-rail-number">
              {sectionNumbers.checkup}
            </div>
          </div>

          <div className="plan-print-other-section-body">
            <div className="plan-print-other-section-title">
              {sectionNumbers.checkup}. {t("plan.printOther.sectionCheckup")}
            </div>

            <div
              className={
                printSections.instrumental
                  ? "plan-print-other-diagnostics"
                  : "plan-print-other-diagnostics plan-print-other-diagnostics--single"
              }>
              <div className="plan-print-other-diagnostics-col">
                <div className="plan-print-other-diagnostics-heading">
                  {t("plan.printOther.laboratory")}
                </div>

                <div className="plan-print-other-diagnostics-list">
                  {checkupItems.map((item, index) => (
                    <div
                      className="plan-print-other-check-item"
                      key={`checkup-${index}-${item.name}`}>
                      <span
                        className="plan-print-other-checkbox plan-print-other-checkbox--filled"
                        aria-hidden="true"
                      />
                      <div className="plan-print-other-check-content">
                        <div className="plan-print-other-check-label">
                          {item.name}
                        </div>
                        {item.subitems.length > 0 ? (
                          <ul className="plan-print-other-check-sublist">
                            {item.subitems.map((subName, subIndex) => (
                              <li key={`checkup-sub-${index}-${subIndex}`}>
                                {subName}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  ))}

                  <div className="plan-print-other-check-item plan-print-other-check-item--extra">
                    <span
                      className="plan-print-other-checkbox"
                      aria-hidden="true"
                    />
                    <div className="plan-print-other-check-content">
                      <div className="plan-print-other-check-label">
                        {t("plan.printOther.extraLabTests")}:
                      </div>
                      <div
                        className="plan-print-other-section-line"
                        aria-hidden="true"
                      />
                      <div
                        className="plan-print-other-section-line"
                        aria-hidden="true"
                      />
                      <div
                        className="plan-print-other-section-line"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>

                <div className="plan-print-other-diagnostics-deadline">
                  <span className="plan-print-other-field-label">
                    {t("plan.printOther.deadline")}:
                  </span>
                  <span className="plan-print-other-field-value">&nbsp;</span>
                </div>
              </div>

              {printSections.instrumental && (
              <div className="plan-print-other-diagnostics-col">
                <div className="plan-print-other-diagnostics-heading">
                  {t("plan.printOther.instrumental")}
                </div>

                <div className="plan-print-other-diagnostics-list">
                  {INSTRUMENTAL_DIAGNOSTICS.map((labelKey) => (
                    <div
                      className="plan-print-other-check-item"
                      key={labelKey}
                    >
                      <span
                        className="plan-print-other-checkbox"
                        aria-hidden="true"
                      />
                      <div className="plan-print-other-check-label">
                        {t(labelKey)}
                      </div>
                    </div>
                  ))}

                  <div className="plan-print-other-check-item plan-print-other-check-item--other">
                    <span
                      className="plan-print-other-checkbox"
                      aria-hidden="true"
                    />
                    <div className="plan-print-other-field plan-print-other-field--full">
                      <span className="plan-print-other-check-label">
                        {t("plan.printOther.other")}:
                      </span>
                      <span className="plan-print-other-field-value">
                        &nbsp;
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.infusion}
            onChange={() => togglePrintSection("infusion")}
          />
          <span>
            {sectionNumbers.infusion ? `${sectionNumbers.infusion}. ` : ""}
            {t("plan.printOther.sectionInfusionLabel")}
          </span>
        </label>
      </div>

      {printSections.infusion && (
        <section className="plan-print-other-section plan-print-other-section-third">
          <div className="plan-print-other-section-rail" aria-hidden="true">
            <svg
              className="plan-print-other-section-rail-icon"
              viewBox="0 0 24 24"
              fill="none">
              <path
                d="M9.5 2.5h5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M12 2.5v2.2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M8.2 4.7h7.6c.9 0 1.6.7 1.6 1.6v7.1c0 2.6-2.1 4.7-4.7 4.7h-1.4c-2.6 0-4.7-2.1-4.7-4.7V6.3c0-.9.7-1.6 1.6-1.6z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 11.2h5.6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M12 18.1v2.2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M12 20.3c1.4 0 2.2.7 2.2 1.2"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            <div className="plan-print-other-section-rail-number">
              {sectionNumbers.infusion}
            </div>
          </div>

          <div className="plan-print-other-section-body plan-print-other-infusion-body">
            <div className="plan-print-other-infusion-main">
              <div className="plan-print-other-section-title">
                {sectionNumbers.infusion}. {t("plan.printOther.sectionInfusion")}
              </div>

              <div className="plan-print-other-infusion-protocol">
                <div className="plan-print-other-field-label">
                  {t("plan.printOther.recommendedProtocol")}:
                </div>
                <div className="plan-print-other-infusion-protocol-list">
                  {infusionTherapy.items.map((item, index) => (
                    <div
                      className="plan-print-other-check-item"
                      key={`infusion-${index}-${item.name}`}>
                      <span
                        className="plan-print-other-checkbox plan-print-other-checkbox--filled"
                        aria-hidden="true"
                      />
                      <div className="plan-print-other-check-content">
                        <div className="plan-print-other-check-label">
                          {item.name}
                        </div>
                        {item.subitems.length > 0 ? (
                          <ul className="plan-print-other-check-sublist">
                            {item.subitems.map((sub, subIndex) => (
                              <li key={`infusion-sub-${index}-${subIndex}`}>
                                {sub.name}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="plan-print-other-infusion-composition">
                <div className="plan-print-other-field-label">
                  {t("plan.printOther.infusionComposition")}:
                </div>
                {infusionCompositionDisplay.map((line, index) => (
                  <div
                    className="plan-print-other-section-line plan-print-other-section-line--filled"
                    key={`infusion-composition-${index}`}>
                    {line || "\u00A0"}
                  </div>
                ))}
              </div>

              <div className="plan-print-other-infusion-metrics">
                <div className="plan-print-other-field plan-print-other-field--volume">
                  <span className="plan-print-other-field-label">
                    {t("plan.printOther.volume")}:
                  </span>
                  <span className="plan-print-other-field-value">&nbsp;</span>
                  <span className="plan-print-other-field-suffix">
                    {t("plan.printOther.ml")}
                  </span>
                </div>
                <div className="plan-print-other-field plan-print-other-field--frequency">
                  <span className="plan-print-other-field-label">
                    {t("plan.printOther.frequency")}:
                  </span>
                  <span className="plan-print-other-field-value">&nbsp;</span>
                </div>
                <div className="plan-print-other-field plan-print-other-field--procedures">
                  <span className="plan-print-other-field-label">
                    {t("plan.printOther.proceduresCount")}:
                  </span>
                  <span className="plan-print-other-field-value">
                    {infusionTherapy.proceduresCount > 0
                      ? infusionTherapy.proceduresCount
                      : "\u00A0"}
                  </span>
                </div>
              </div>
            </div>

            <aside className="plan-print-other-infusion-note">
              <div className="plan-print-other-infusion-note-header">
                <span
                  className="plan-print-other-infusion-note-icon"
                  aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect
                      x="4.5"
                      y="5"
                      width="15"
                      height="15"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M8 3.5v3M16 3.5v3M4.5 9.5h15"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span className="plan-print-other-infusion-note-title">
                  {t("plan.printOther.doctorNote")}:
                </span>
              </div>
              <div className="plan-print-other-infusion-note-lines">
                <div
                  className="plan-print-other-section-line"
                  aria-hidden="true"
                />
                <div
                  className="plan-print-other-section-line"
                  aria-hidden="true"
                />
                <div
                  className="plan-print-other-section-line"
                  aria-hidden="true"
                />
                <div
                  className="plan-print-other-section-line"
                  aria-hidden="true"
                />
              </div>
              <div className="plan-print-other-infusion-note-footer">
                {t("plan.printOther.infusionNoteFooter")}
              </div>
            </aside>
          </div>
        </section>
      )}

      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.meds}
            onChange={() => togglePrintSection("meds")}
          />
          <span>
            {sectionNumbers.meds ? `${sectionNumbers.meds}. ` : ""}
            {t("plan.printOther.sectionMedsLabel")}
          </span>
        </label>
      </div>

      {printSections.meds && (
        <section className="plan-print-other-section plan-print-other-section-fourth">
          <div className="plan-print-other-section-rail" aria-hidden="true">
            <svg
              className="plan-print-other-section-rail-icon"
              viewBox="0 0 24 24"
              fill="none">
              <rect
                x="3.2"
                y="8.2"
                width="10.2"
                height="5.2"
                rx="2.6"
                stroke="currentColor"
                strokeWidth="1.7"
                transform="rotate(-35 8.3 10.8)"
              />
              <path
                d="M6.1 12.3l3.4-2.4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <rect
                x="10.6"
                y="10.6"
                width="10.2"
                height="5.2"
                rx="2.6"
                stroke="currentColor"
                strokeWidth="1.7"
                transform="rotate(28 15.7 13.2)"
              />
              <path
                d="M14.2 11.8l3.2 2.1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <div className="plan-print-other-section-rail-number">
              {sectionNumbers.meds}
            </div>
          </div>

          <div className="plan-print-other-section-body">
            <div className="plan-print-other-section-title">
              {sectionNumbers.meds}. {t("plan.printOther.sectionMeds")}
            </div>

            <table className="plan-print-other-meds-table">
              <thead>
                <tr>
                  <th>{t("plan.printOther.medication")}</th>
                  <th>{t("plan.printOther.dosage")}</th>
                  <th>{t("plan.printOther.regimen")}</th>
                  <th>{t("plan.printOther.course")}</th>
                </tr>
              </thead>
              <tbody>
                {oralMedicationRows.map((item, index) => (
                  <tr key={`oral-med-${index}-${item.name || "empty"}`}>
                    <td>{item.name || "\u00A0"}</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="plan-print-other-section-row plan-print-other-meds-extra">
              <div className="plan-print-other-field plan-print-other-field--full">
                <span className="plan-print-other-field-label">
                  {t("plan.printOther.extraRecommendations")}:
                </span>
                <span className="plan-print-other-field-value">&nbsp;</span>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.schedule}
            onChange={() => togglePrintSection("schedule")}
          />
          <span>
            {sectionNumbers.schedule ? `${sectionNumbers.schedule}. ` : ""}
            {t("plan.printOther.sectionScheduleLabel")}
          </span>
        </label>
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.scheduleHideEmpty}
            onChange={() => togglePrintSection("scheduleHideEmpty")}
            disabled={!printSections.schedule}
          />
          <span>{t("plan.printOther.hideEmptyRows")}</span>
        </label>
      </div>

      {printSections.schedule && (
        <section className="plan-print-other-section plan-print-other-section-fifth">
          <div className="plan-print-other-section-rail" aria-hidden="true">
            <svg
              className="plan-print-other-section-rail-icon"
              viewBox="0 0 24 24"
              fill="none">
              <rect
                x="4.5"
                y="5"
                width="15"
                height="15"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M8 3.5v3M16 3.5v3M4.5 9.5h15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M9 13h2.2v4.5M14.8 13v2.2c0 1.2-.9 2.3-2.2 2.3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="plan-print-other-section-rail-number">
              {sectionNumbers.schedule}
            </div>
          </div>

          <div className="plan-print-other-section-body">
            <div className="plan-print-other-section-title">
              {sectionNumbers.schedule}. {t("plan.printOther.sectionSchedule")}
            </div>

            <table className="plan-print-other-schedule-table">
              <thead>
                <tr>
                  <th className="plan-print-other-schedule-label-col">
                    {t("plan.printOther.week")}
                  </th>
                  {scheduleWeeks.map((week) => (
                    <th
                      key={`schedule-week-${week}`}
                      className={
                        week % 4 === 0
                          ? "plan-print-other-schedule-week--gold"
                          : undefined
                      }>
                      {week}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleScheduleRows.map((row) => (
                  <tr key={row.key}>
                    <td className="plan-print-other-schedule-label-col">
                      <span className="plan-print-other-schedule-row-label">
                        <span
                          className="plan-print-other-schedule-row-icon"
                          aria-hidden="true">
                          <ScheduleRowIcon type={row.icon} />
                        </span>
                        <span>{t(row.labelKey)}</span>
                      </span>
                    </td>
                    {scheduleWeeks.map((week) => {
                      const isMarked = row.markedWeeks.has(week);
                      const isHighlighted =
                        row.highlightMilestones && isMilestoneWeek(week);
                      return (
                        <td
                          key={`${row.key}-week-${week}`}
                          className={
                            isHighlighted
                              ? "plan-print-other-schedule-cell--highlight"
                              : undefined
                          }>
                          <span
                            className={
                              isMarked
                                ? "plan-print-other-schedule-check plan-print-other-schedule-check--filled"
                                : "plan-print-other-schedule-check"
                            }
                            aria-hidden="true"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="plan-print-other-schedule-notes">
              <div className="plan-print-other-field plan-print-other-field--full">
                <span className="plan-print-other-field-label">
                  {t("plan.printOther.protocolFeatures")}:
                </span>
                <span className="plan-print-other-field-value">&nbsp;</span>
              </div>
              <div className="plan-print-other-schedule-correction">
                <div className="plan-print-other-field plan-print-other-field--correction-week">
                  <span className="plan-print-other-field-label">
                    {t("plan.printOther.correctionFrom")}
                  </span>
                  <span className="plan-print-other-field-value">&nbsp;</span>
                  <span className="plan-print-other-field-label">
                    {t("plan.printOther.correctionWeeks")}
                  </span>
                </div>
                <div className="plan-print-other-field plan-print-other-field--full">
                  <span className="plan-print-other-field-value">&nbsp;</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="plan-print-other-followup">
        <div className="plan-print-other-followup-card">
          <span className="plan-print-other-followup-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="7.5"
                r="3.2"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M5.2 19.5c1.1-3.5 3.4-5.2 6.8-5.2s5.7 1.7 6.8 5.2"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
              <path
                d="M9.2 12.8l1.4 1.6M14.8 12.8l-1.4 1.6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div className="plan-print-other-followup-content">
            <div className="plan-print-other-followup-title">
              {t("plan.printOther.followupConsultation")}
            </div>
            <div className="plan-print-other-followup-date">
              <span>«</span>
              <span className="plan-print-other-followup-date-day">&nbsp;</span>
              <span>»</span>
              <span className="plan-print-other-followup-date-month">
                &nbsp;
              </span>
              <span>20</span>
              <span className="plan-print-other-followup-date-year">
                &nbsp;
              </span>
              <span>{t("plan.printOther.yearShort")}</span>
            </div>
          </div>
        </div>

        <div className="plan-print-other-followup-card">
          <span className="plan-print-other-followup-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <rect
                x="5"
                y="3.5"
                width="14"
                height="17"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M9 2.8h6v2.4H9V2.8z"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M8.5 10h7M8.5 13.2h7M8.5 16.4h4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <div className="plan-print-other-followup-content">
            <div className="plan-print-other-followup-title">
              {t("plan.printOther.followupNext")}
            </div>
            <div className="plan-print-other-followup-text">
              {t("plan.printOther.followupNextText")}
            </div>
          </div>
        </div>
      </div>

      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.scheduleDetailed}
            onChange={() => togglePrintSection("scheduleDetailed")}
          />
          <span>
            {sectionNumbers.scheduleDetailed
              ? `${sectionNumbers.scheduleDetailed}. `
              : ""}
            {t("plan.printOther.sectionScheduleDetailed")}
          </span>
        </label>
      </div>

      {printSections.scheduleDetailed && (
        <div className="plan-print-other-new-page">
          <header className="plan-print-other-page-banner">
            <div className="plan-print-other-header-brand">
              <PlanPrintOtherLogoIcon />
              <div className="plan-print-other-brand-text">
                <div className="plan-print-other-brand-name">REFRESH</div>
                <div className="plan-print-other-brand-sub">
                  LONGEVITY CLINIC
                </div>
              </div>
            </div>
            <div className="plan-print-other-page-banner-title">
              {t("plan.printOther.patientRecommendationsSheet")}
            </div>
            <img
              src={dnkIcon}
              alt=""
              className="plan-print-other-page-banner-dna"
              aria-hidden="true"
            />
          </header>

          <section className="plan-print-other-section plan-print-other-section-sixth">
            <div className="plan-print-other-section-rail" aria-hidden="true">
              <svg
                className="plan-print-other-section-rail-icon"
                viewBox="0 0 24 24"
                fill="none">
                <rect
                  x="4.5"
                  y="5"
                  width="15"
                  height="15"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path
                  d="M8 3.5v3M16 3.5v3M4.5 9.5h15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M8.2 13h2.2M8.2 16.2h7.6M13.2 13h2.6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <div className="plan-print-other-section-rail-number">
                {sectionNumbers.scheduleDetailed}
              </div>
            </div>

            <div className="plan-print-other-section-body">
              <div className="plan-print-other-section-title">
                {sectionNumbers.scheduleDetailed}. {t("plan.printOther.sectionSchedule")}
              </div>

              <table className="plan-print-other-schedule-table plan-print-other-schedule-table--detailed">
                <thead>
                  <tr>
                    <th className="plan-print-other-schedule-label-col">
                      {t("plan.printOther.week")}
                    </th>
                    {scheduleWeeks.map((week) => (
                      <th
                        key={`plan-schedule-week-${week}`}
                        className={
                          week % 4 === 0
                            ? "plan-print-other-schedule-week--gold"
                            : undefined
                        }>
                        {week}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {planScheduleSections.length === 0 ? (
                    <tr>
                      <td
                        className="plan-print-other-schedule-label-col"
                        colSpan={scheduleWeeks.length + 1}>
                        <span className="plan-print-other-schedule-empty">
                          {t("plan.printOther.noServices")}
                        </span>
                      </td>
                    </tr>
                  ) : (
                    planScheduleSections.map((section) => (
                      <React.Fragment key={section.key}>
                        <tr className="plan-print-other-schedule-group-row">
                          <td colSpan={scheduleWeeks.length + 1}>
                            {section.groupName}
                          </td>
                        </tr>
                        {section.items.map((item) => (
                          <tr key={item.key}>
                            <td className="plan-print-other-schedule-label-col">
                              <div className="plan-print-other-schedule-service">
                                <span className="plan-print-other-schedule-row-label">
                                  <span
                                    className="plan-print-other-schedule-row-icon"
                                    aria-hidden="true">
                                    <ScheduleRowIcon type={item.icon} />
                                  </span>
                                  <span>{item.name}</span>
                                </span>
                                {item.composition ? (
                                  <div className="plan-print-other-schedule-composition">
                                    {item.composition}
                                  </div>
                                ) : null}
                                {item.subitems.length > 0 ? (
                                  <ul className="plan-print-other-schedule-sublist">
                                    {item.subitems.map((sub, subIndex) => (
                                      <li
                                        key={`${item.key}-sub-${subIndex}-${sub.name}`}>
                                        <span>{sub.name}</span>
                                        {sub.composition ? (
                                          <span className="plan-print-other-schedule-composition">
                                            {sub.composition}
                                          </span>
                                        ) : null}
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                            </td>
                            {scheduleWeeks.map((week) => {
                              const isMarked = item.markedWeeks.has(week);
                              const isHighlighted =
                                item.highlightMilestones &&
                                isMilestoneWeek(week);
                              return (
                                <td
                                  key={`${item.key}-week-${week}`}
                                  className={
                                    isHighlighted
                                      ? "plan-print-other-schedule-cell--highlight"
                                      : undefined
                                  }>
                                  <span
                                    className={
                                      isMarked
                                        ? "plan-print-other-schedule-check plan-print-other-schedule-check--filled"
                                        : "plan-print-other-schedule-check"
                                    }
                                    aria-hidden="true"
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>

              <div className="plan-print-other-schedule-notes">
                <div className="plan-print-other-field plan-print-other-field--full">
                  <span className="plan-print-other-field-label">
                    {t("plan.printOther.protocolFeatures")}:
                  </span>
                  <span className="plan-print-other-field-value">&nbsp;</span>
                </div>
                <div className="plan-print-other-schedule-correction">
                  <div className="plan-print-other-field plan-print-other-field--correction-week">
                    <span className="plan-print-other-field-label">
                      {t("plan.printOther.correctionFrom")}
                    </span>
                    <span className="plan-print-other-field-value">&nbsp;</span>
                    <span className="plan-print-other-field-label">
                      {t("plan.printOther.correctionWeeks")}
                    </span>
                  </div>
                  <div className="plan-print-other-field plan-print-other-field--full">
                    <span className="plan-print-other-field-value">&nbsp;</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.recommendations}
            onChange={() => togglePrintSection("recommendations")}
          />
          <span>
            {sectionNumbers.recommendations
              ? `${sectionNumbers.recommendations}. `
              : ""}
            {t("plan.buttons.recommendations")}
          </span>
        </label>
      </div>

      {printSections.recommendations && (
        <section className="plan-print-other-section plan-print-other-section-seventh">
          <div className="plan-print-other-section-rail" aria-hidden="true">
            <svg
              className="plan-print-other-section-rail-icon"
              viewBox="0 0 24 24"
              fill="none">
              <path
                d="M8 5.5h10M8 10h10M8 14.5h6.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M4.6 5.2l1.3 1.3 2.1-2.4M4.6 9.7l1.3 1.3 2.1-2.4M4.6 14.2l1.3 1.3 2.1-2.4"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="plan-print-other-section-rail-number">
              {sectionNumbers.recommendations}
            </div>
          </div>

          <div className="plan-print-other-section-body">
            <div className="plan-print-other-section-title">
              {sectionNumbers.recommendations}. {t("usi.sectionRecommendations")}
            </div>

            {recommendationRows.length === 0 ? (
              <div className="plan-print-other-recommendation-empty">
                {t("plan.printOther.recommendationsNotAssigned")}
              </div>
            ) : (
              <div className="plan-print-other-recommendations">
                {recommendationRows.map((row, rowIndex) => (
                  <div
                    className={
                      row.length === 2
                        ? "plan-print-other-recommendations-row plan-print-other-recommendations-row--pair"
                        : "plan-print-other-recommendations-row"
                    }
                    key={`recommendation-row-${rowIndex}`}>
                    {row.map((item, itemIndex) => (
                      <div
                        className="plan-print-other-recommendation"
                        key={
                          item.id || `recommendation-${rowIndex}-${itemIndex}`
                        }>
                        <div className="plan-print-other-recommendation-title">
                          {item.title}
                        </div>
                        {item.html ? (
                          <div
                            className="plan-print-other-recommendation-body"
                            dangerouslySetInnerHTML={{ __html: item.html }}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="plan-print-other-advice">
        <div className="plan-print-other-advice-block">
          <div className="plan-print-other-advice-heading">
            {t("plan.printOther.regimeHeading")}
          </div>
          <div className="plan-print-other-advice-card">
            <div className="plan-print-other-regime">
              {REGIME_ITEMS.map((item) => (
                <div className="plan-print-other-regime-item" key={item.key}>
                  <span
                    className="plan-print-other-regime-icon"
                    aria-hidden="true">
                    <RegimeItemIcon type={item.icon} />
                  </span>
                  <div className="plan-print-other-regime-text">
                    {t(item.textKey)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="plan-print-other-advice-block">
          <div className="plan-print-other-advice-heading">
            {t("plan.printOther.doctorRecommendationsHeading")}
          </div>
          <div className="plan-print-other-advice-card">
            <div className="plan-print-other-doctor-rec">
              <div className="plan-print-other-doctor-rec-col">
                <div className="plan-print-other-doctor-rec-row">
                  <span className="plan-print-other-doctor-rec-label">
                    {t("plan.printOther.targetCalories")}:
                  </span>
                  <span
                    className="plan-print-other-doctor-rec-line"
                    aria-hidden="true"
                  />
                  <span className="plan-print-other-doctor-rec-unit">
                    {t("plan.printOther.kcalPerDay")}
                  </span>
                </div>
                <div className="plan-print-other-doctor-rec-row">
                  <span className="plan-print-other-doctor-rec-label">
                    {t("plan.printOther.protein")}:
                  </span>
                  <span
                    className="plan-print-other-doctor-rec-line"
                    aria-hidden="true"
                  />
                  <span className="plan-print-other-doctor-rec-unit">
                    {t("plan.printOther.gPerDay")}
                  </span>
                </div>
                <div className="plan-print-other-doctor-rec-row">
                  <span className="plan-print-other-doctor-rec-label">
                    {t("plan.printOther.water")}:
                  </span>
                  <span
                    className="plan-print-other-doctor-rec-line"
                    aria-hidden="true"
                  />
                  <span className="plan-print-other-doctor-rec-unit">
                    {t("plan.printOther.mlPerDay")}
                  </span>
                </div>
              </div>

              <div className="plan-print-other-doctor-rec-col">
                <div className="plan-print-other-doctor-rec-stack">
                  <span className="plan-print-other-doctor-rec-label">
                    {t("plan.printOther.dietRestrictions")}:
                  </span>
                  <span
                    className="plan-print-other-doctor-rec-line plan-print-other-doctor-rec-line--full"
                    aria-hidden="true"
                  />
                  <span
                    className="plan-print-other-doctor-rec-line plan-print-other-doctor-rec-line--full"
                    aria-hidden="true"
                  />
                </div>
                <div className="plan-print-other-doctor-rec-row">
                  <span className="plan-print-other-doctor-rec-label">
                    {t("plan.printOther.extraRecommendations")}:
                  </span>
                  <span
                    className="plan-print-other-doctor-rec-line"
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="plan-print-other-important">
        <div className="plan-print-other-important-col plan-print-other-important-col--left">
          <span className="plan-print-other-important-icon" aria-hidden="true">
            <ImportantClipboardIcon />
          </span>
          <div className="plan-print-other-important-copy">
            <div className="plan-print-other-important-title">
              {t("plan.printOther.important")}
            </div>
            <div className="plan-print-other-important-text">
              {t("plan.printOther.importantText")}
            </div>
          </div>
        </div>

        <div
          className="plan-print-other-important-divider"
          aria-hidden="true"
        />

        <div className="plan-print-other-important-col plan-print-other-important-col--brand">
          <span className="plan-print-other-important-brand">Refresh</span>
        </div>

        <div
          className="plan-print-other-important-divider"
          aria-hidden="true"
        />

        <div className="plan-print-other-important-col plan-print-other-important-col--right">
          <div className="plan-print-other-important-text">
            {t("plan.printOther.supportText")}
          </div>
          <span
            className="plan-print-other-important-branch"
            aria-hidden="true">
            <ImportantBranchIcon />
          </span>
        </div>
      </div>

      <div className="plan-print-other-signature">
        <div className="plan-print-other-signature-doctor">
          <span className="plan-print-other-signature-label">
            {t("usi.doctor")}:
          </span>
          <div className="plan-print-other-signature-field plan-print-other-signature-field--fio">
            <span className="plan-print-other-signature-line">
              {doctorFio !== "—" ? doctorFio : "\u00A0"}
            </span>
            <span className="plan-print-other-signature-hint">
              {t("usi.signatureFio")}
            </span>
          </div>
          <span className="plan-print-other-signature-sep">/</span>
          <div className="plan-print-other-signature-field plan-print-other-signature-field--sign">
            <span className="plan-print-other-signature-line">&nbsp;</span>
            <span className="plan-print-other-signature-hint">
              {t("usi.signatureSign")}
            </span>
          </div>
        </div>
        <div className="plan-print-other-signature-date">
          <span className="plan-print-other-signature-label">
            {t("usi.dateLabel")}
          </span>
          <span className="plan-print-other-signature-line plan-print-other-signature-line--date">
            {signatureDate}
          </span>
        </div>
      </div>

      {includeSmartDiagnostics && smartDiagnosticsMeta ? (
        <div className="plan-print-other-new-page patient-smart-diagnostics-print">
          <SmartDiagnosticsPrintSheet
            formData={smartDiagnosticsForm}
            appointments={appointments}
            docMeta={smartDiagnosticsMeta}
          />
        </div>
      ) : null}
    </div>
  );
}

export default PatientPlanPrintOtherFormat;
